import { useTranslations } from "next-intl";
import { FC, useState } from "react";
import { Container, InfoTextBox, SpinningIcon, StyledButton } from "./styles";
import { Typography } from "@mui/material";
import { COLORS } from "@/constants";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { isNotEmptyField } from "@/common";
import { useGetBrokerContactsInfo } from "@/queris";
import { Link } from "@/components/shared/UI/Link";
import { pushClickMail, pushClickPhone, pushCopyMail, pushShowContactInfo } from "@/lib/analytics";

type SuccessParams = {
  mobile?: string;
  phone?: string;
  email?: string;
};

type ProtectedInformationProps = SuccessParams & {
  objectId: string;
}

export const ProtectedInformation: FC<ProtectedInformationProps> = ({
  mobile,
  phone,
  email,
  objectId,
}) => {
  const t = useTranslations();

  const [fullContacts, setFullContacts] = useState<SuccessParams | null>(null)

  const { isPending, mutate } = useGetBrokerContactsInfo(
    (data) => {
      setFullContacts(data)
    },
  );

  const handleGetInformation = () => {
    pushShowContactInfo();
    mutate(
      { objectId },
    )
  }

  const telHref = (n?: string) => (n ? `tel:${n.replace(/\s/g, '')}` : undefined);

  const isRevealed = Boolean(fullContacts)

  const maskValue = (value?: string) => (value ? `${value}***` : '')

  const displayMobile = isRevealed ? fullContacts?.mobile : maskValue(mobile)
  const displayPhone = isRevealed ? fullContacts?.phone : maskValue(phone)
  const displayEmail = isRevealed ? fullContacts?.email : maskValue(email)

  return (
    <Container>
      {isNotEmptyField(phone) && (
        <InfoTextBox>
          <Typography
            fontSize={14}
            fontWeight={400}
            lineHeight="125%"
            color={COLORS['neutral/600']}
          >
            {t('housingCardDetails.tel')}:
          </Typography>
          <Typography fontSize={14} fontWeight={600} lineHeight="125%">
            {isRevealed && fullContacts?.phone ? (
              <Link
                href={telHref(fullContacts.phone)!}
                onClick={() => pushClickPhone(fullContacts.phone!.replace(/\s/g, ''))}
              >
                {displayPhone}
              </Link>
            ) : (
              displayPhone
            )}
          </Typography>
        </InfoTextBox>
      )}

      {isNotEmptyField(mobile) && (
        <InfoTextBox>
          <Typography
            fontSize={14}
            fontWeight={400}
            lineHeight="125%"
            color={COLORS['neutral/600']}
          >
            {t('housingCardDetails.mob')}:
          </Typography>
          <Typography fontSize={14} fontWeight={600} lineHeight="125%">
            {isRevealed && fullContacts?.mobile ? (
              <Link
                href={telHref(fullContacts.mobile)!}
                onClick={() => pushClickPhone(fullContacts.mobile!.replace(/\s/g, ''))}
              >
                {displayMobile}
              </Link>
            ) : (
              displayMobile
            )}
          </Typography>
        </InfoTextBox>
      )}

      {isNotEmptyField(email) && (
        <InfoTextBox>
          <Typography
            fontSize={14}
            fontWeight={400}
            lineHeight="125%"
            color={COLORS['neutral/600']}
          >
            {t('housingCardDetails.email')}:
          </Typography>
          <Typography fontSize={14} fontWeight={600} lineHeight="125%">
            {isRevealed && fullContacts?.email ? (
              <Link
                href={`mailto:${fullContacts.email}`}
                onClick={() => pushClickMail(fullContacts.email!)}
                onCopy={() => pushCopyMail(fullContacts.email!)}
              >
                {displayEmail}
              </Link>
            ) : (
              displayEmail
            )}
          </Typography>
        </InfoTextBox>
      )}

      {!isRevealed && (
        <StyledButton
          size="small"
          startIcon={
            isPending ? (
              <SpinningIcon fontSize="small" />
            ) : (
              <RemoveRedEyeIcon fontSize="small" />
            )
          }
          onClick={handleGetInformation}
          disabled={isPending}
        >
          {isPending ? t('loading') : t('housingCardDetails.showInfo')}
        </StyledButton>
      )}
    </Container>
  )
}
